<script>
import { mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import VFormField from '@/components/VFormField'
import VSubmitButton from '@/components/VSubmitButton'
import VImageTransition from '@/components/VImageTransition'
import api from '@/api'
import utils from '@/utils'
const VWarningMessage = () => import('@/components/VWarningMessage')

export default {
  name: 'ResetPasswordScreenForm',

  components: {
    VFormField,
    VWarningMessage,
    VSubmitButton,
    VImageTransition,
  },

  props: {
    token: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      icon: 'visibility',
      passwordType: 'password',
      passwordIconActive: false,
      passwordValue: '',
      repeatPasswordValue: '',
      warningMessage: '',
      warningLink: '',
      validationMessage: '',
      isPasswordValidated: true,
      isSubmittButtonDisabled: false,
    }
  },

  computed: {
    ...mapState({
      clientLogo: 'clientLogo',
      clientName: 'clientName',
    }),
  },

  beforeMount() {
    api
      .verifyToken(this.token)
      .catch(() => this.warningMessage = 'recoverTokenExpired')
  },

  validations: {
    passwordValue: {
      required(value) {
        if (required(value)) return true
        this.validationMessage = this.$t('requiredField')
        this.$v.passwordValue.$touch()
        return false
      },
      weak() {
        return this.isPasswordValidated
      },
    },
    repeatPasswordValue: {
      required(value) {
        if (required(value)) return true
        this.validationMessage = this.$t('requiredField')
        return false
      },
      sameAsPassword(value) {
        if(value === this.passwordValue) {
          this.isPasswordValidated = true
          return true
        }
        this.validationMessage = this.$t('differentPassword')
        return false
      },
      weak() {
        return this.isPasswordValidated
      },
    }
  },

  methods: {
    togglePassword() {
      this.passwordType = this.passwordType === 'password' ? 'text' : 'password'
      this.passwordIconActive = !this.passwordIconActive
    },

    setPasswordAsValid() {
      this.$router.push({ name: 'successResetPassword' })
      this.isPasswordValidated = true
    },

    setPasswordAsNotValid(error) {
      if (this.isPasswordValidationError(error)) {
        return this.setFieldValidationMessage(error.keyMessage);
      }
      this.setWarningMessage('createNewPasswordFail', 'createNewPasswordSupportContact')
    },

    setWarningMessage(message, link = '') {
      this.warningMessage = message
      this.warningLink = link
    },

    setFieldValidationMessage(message) {
      this.isPasswordValidated = false
      this.validationMessage = this.$t(message)
    },

    isPasswordValidationError(error) {
      const isPasswordValidationError = error && error.code === 400 && error.keyMessage
      return isPasswordValidationError
    },

    isFormInvalid() {
      this.$v.$touch()
      return this.$v.$error
    },

    async resetPassword() {
      if (this.isFormInvalid()) return;

      this.$store.dispatch('START_LOADING')
      api.resetPassword(this.passwordValue, this.token)
        .then(() => {
          this.$store.dispatch('STOP_LOADING')
          this.setPasswordAsValid()
        })
        .catch((error) => {
          this.$store.dispatch('STOP_LOADING')
          this.setPasswordAsNotValid(error)
        })
    },
  },
}
</script>

<template>
  <form
    class="reset-password"
    @submit.prevent="resetPassword"
  >

    <div class="reset-password__logo-content">
      <VImageTransition
        :image="clientLogo"
        :text="clientName"
        small
      />
    </div>

    <div class="reset-password__content">
      <div class="reset-password__warning-content">
        <VWarningMessage
          v-if="warningMessage"
          class="reset-password__warning"
          error
        >
          <i18n :path="warningMessage">
            <router-link
              :to="{ name: 'forgotPassword' }"
              place="link"
              class="reset-password__warning-link"
            >
              <span>{{ $t(warningLink || 'recoverPasswordNewRequest') }}</span>
            </router-link>
          </i18n>
        </VWarningMessage>
      </div>

      <div class="reset-password__fields">
        <div class="reset-password__title">
          {{ $t('createNewPassword') }}
        </div>

        <div class="reset-password__description">
          {{ $t('createPasswordDescription') }}
        </div>

        <VFormField
          :label="$i18n.t('password')"
          :type="passwordType"
          :icon="icon"
          :icon-action="togglePassword"
          :icon-active="passwordIconActive"
          v-model="passwordValue"
          :validation="$v.passwordValue"
          :validation-message="validationMessage"
          name="password"
          autofocus
        />

        <VFormField
          :label="$i18n.t('repeatPassword')"
          :type="passwordType"
          :icon="icon"
          :icon-action="togglePassword"
          :icon-active="passwordIconActive"
          v-model="repeatPasswordValue"
          :validation="$v.repeatPasswordValue"
          :validation-message="validationMessage"
          name="resetPassword"
        />
      </div>
    </div>

    <div class="reset-password__footer">
      <VSubmitButton
        :disabled="$v.$error"
        class="reset-password__footer-submit"
      >
        {{ $t('createPassword') }}
      </VSubmitButton>

      <router-link
        :to="{ name: 'loginScreen' }"
        class="inv-text-link"
      >
        {{ $t('backToMainScreen') }}
      </router-link>
    </div>
  </form>
</template>

<style scoped>
.reset-password {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 28px;
  margin-bottom: var(--space-24);
}

.reset-password__content {
  max-width: 288px;
  width: 100%;
  height: 325px;
}

.reset-password__logo-content {
  min-height: 56px;
  max-height: 56px;
}

.reset-password__warning-content {
  min-height: 70px;
  padding-top: 16px;
}

.reset-password__fields {
  display: flex;
  flex-direction: column;
  align-content: center;
  min-height: 230px;
  margin-bottom: 12px;
}

.reset-password__warning + .reset-password__fields {
  margin-top: 12px;
}

.reset-password__warning-link {
  color: var(--color-error--dark);
}

.reset-password__title {
  text-align: center;
  font-weight: bold;
  font-size: 18px;
  color: var(--color-default--dark);
}

.reset-password__description {
  max-width: 190px;
  margin-top: 6px;
  margin-bottom: 6px;
  font-size: 14px;
  color: var(--color-system--dark);
  align-self: center;
  text-align: center;
  line-height: 19px;
}

.reset-password__footer {
  text-align: center;
  max-width: 288px;
  width: 100%;
}

.reset-password__footer-submit {
  margin-bottom: 24px;
}

.inv-form-field + .inv-form-field {
  margin-top: var(--space-16)
}

::v-deep(.reset-password__footer-submit button) {
  margin-top: var(--space-24)
}

</style>
